<template>
  <div>
    <Spinner v-if="fetching" />
    <div v-else class="container-fluid plr-80 payment-page">
      <RateExpiringModal
        @refreshAvailability="handleExpiredAcknowledge"
        ref="rateExpiringModal"
      />
      <div
        v-if="!hasReservationExpired && false"
        class="alert alert-warning d-flex justify-content-between align-items-center mt-3 mb-4"
      >
        <span>Time left to complete payment</span>
        <strong>{{ formattedTimeLeft }}</strong>
      </div>
      <div class="mb-5">
        <BookingEngineDateSelector
          :promo-disabled="checkoutData.distributorType !== 'A'"
          :checkIn="formatDate(checkoutData.date.checkIn)"
          :checkOut="formatDate(checkoutData.date.checkOut)"
          :currencyCode="checkoutData.property?.homeCurrency?.code"
          :currencySymbol="checkoutData.property?.homeCurrency?.symbol"
          :distributorType="checkoutData.distributorType"
          :guestData="guestData"
          @search="searchAvailability"
        />
      </div>

      <div class="card my-3 g-card">
        <div class="card-body px-md-5 px-2">
          <div
            class="tabs d-flex justify-content-between align-items-center py-3"
          >
            <div class="tab-section completed w-100">
              <div class="tab-icon"></div>
            </div>
            <div
              v-if="!noExtra"
              class="tab-section w-100"
              :class="{
                current: step === 'extra',
                completed: step === 'summary',
              }"
            >
              <p v-if="step === 'extra'" class="tab-label">
                {{ $t("reservation.extra") }}
              </p>
              <div class="tab-icon"></div>
            </div>
            <div
              class="tab-section w-100"
              :class="{ current: step === 'summary' }"
            >
              <p v-if="step === 'summary'" class="tab-label">
                {{ $t("reservation.summary") }}
              </p>
              <div class="tab-icon"></div>
            </div>
            <div class="tab-section">
              <div class="tab-icon"></div>
            </div>
          </div>
        </div>
      </div>
      <div v-if="step === 'extra'">
        <div class="extra-list mt-5">
          <div v-for="extra in extras" :key="extra.id" class="card my-4 extra">
            <div class="card-body row px-md-5 px-2">
              <div class="col-md-4 my-2">
                <div class="d-flex align-items-center">
                  <div class="mr-3">
                    <template v-if="extra.media.length">
                      <div>
                        <img
                          :src="extraImages(extra)[0].url"
                          alt=""
                          class="img-fluid"
                        />
                      </div>
                    </template>
                    <img
                      v-else
                      src="../assets/images/default-extra.png"
                      class="img-fluid"
                      alt=""
                    />
                  </div>
                  <div>{{ extra.productDescription.name }}</div>
                </div>
              </div>
              <div class="col-md-2 col-6 my-auto">
                {{ $t(`reservation.${extra.extraCode}`) }}
              </div>
              <div
                class="col-md-2 col-6 my-auto d-md-block d-none"
                v-if="getExtraValue(extra.id) && !extra.isMandatory"
              >
                <div>{{ $t("reservation.quantity") }}</div>
                <Multiselect
                  placeholder=""
                  style="width: 70px"
                  class="mt-3"
                  :options="cultswitchQtyOption(extra)"
                  :showLabels="false"
                  :searchable="false"
                  :allow-empty="false"
                  @select="extraQuantityChanged($event, extra)"
                  :value="getExtraValue(extra.id)"
                >
                </Multiselect>
              </div>
              <div
                class="text-main font-weight-bold col-md-2 col-6 my-auto text-md-left text-right"
              >
                {{ extra.currencyCode }}
                <span v-if="getExtraValue(extra.id)">{{
                  (cultswitchProductPrice(extra) * getExtraValue(extra.id))
                    | formatPrice
                }}</span>
                <span v-else>{{
                  cultswitchProductPrice(extra) | formatPrice
                }}</span>
              </div>
              <div
                class="col-6 d-md-none d-block"
                v-if="getExtraValue(extra.id)"
              >
                <Multiselect
                  placeholder=""
                  style="width: 70px"
                  class="mt-3"
                  :options="cultswitchQtyOption(extra)"
                  :showLabels="false"
                  :searchable="false"
                  :allow-empty="false"
                  @select="extraQuantityChanged($event, extra)"
                  :value="getExtraValue(extra.id)"
                >
                </Multiselect>
              </div>
              <div v-if="!extra.isMandatory" class="col-md-2 col-6 text-right my-auto ml-auto">
                <button
                  v-if="!getExtraValue(extra.id)"
                  @click="addExtra(extra)"
                  class="btn g-primary-button px-5 mt-md-0 mt-3"
                >
                  + {{ $t("reservation.add") }}
                </button>
                <button
                  v-else
                  @click="removeExtra(extra)"
                  class="btn g-primary-button px-5 mt-md-0 mt-3"
                >
                  {{ $t("reservation.remove") }}
                </button>
              </div>
              <div v-else class="col-md-2 col-6 text-right my-auto ml-auto">
                <button
                  disabled
                  class="btn g-primary-button px-3 mt-md-0 mt-3"
                >
                  {{ $t("reservation.mandatory") }}
                </button>
              </div>
            </div>
          </div>
        </div>
      </div>

      <div v-if="step === 'summary'">
        <CultswitchCheckout
          v-if="checkoutData.distributorType === 'A'"
          :checkoutData="checkoutData"
          ref="cultswitchForm"
          @extrasUpdated="handleExtrasUpdated"
          @roomsUpdated="handleRoomsUpdated"
          @completeBooking="completeBooking"
          :collectCardDetails="collectCardDetails"
          @formInvalid="processingBooking = false"
        />
        <NuiteeCheckout
          v-else-if="
            checkoutData.distributorType === 'B' &&
            checkoutData.provider === 'NUITEE'
          "
          :checkoutData="checkoutData"
          :collectCardDetails="collectCardDetails"
          ref="nuiteeForm"
          @formInvalid="processingBooking = false"
        />
        <EhotelCheckout
          v-else-if="checkoutData.distributorType === 'B'"
          :checkoutData="checkoutData"
          :collectCardDetails="collectCardDetails"
          ref="ehotelForm"
          @completeBooking="completeBooking"
          @formInvalid="processingBooking = false"
        />
      </div>

      <BookingCheckout
        ref="bookingCheckout"
        class="my-md-5"
        :type="checkoutData.distributorType"
        :cart="checkoutData.cart"
        :extras="checkoutData.extras"
        :sticky="false"
        :buttonText="
          step === 'extra' ? $t('property.summary') : $t('property.bookNow')
        "
        @proceed="proceedWithBooking"
        :disabled="processingBooking"
        @clear="clearSelection"
      />
    </div>
  </div>
</template>
<script>
import { get } from "lodash";
import moment from "moment";
import Spinner from "./Widgets/Spinner.vue";
import { mapState, mapActions } from "vuex";
import Multiselect from "vue-multiselect";
import { paymentExpiryMs } from "../shared";
import BookingCheckout from "./Widgets/BookingCheckout.vue";
import RateExpiringModal from "./Widgets/RateExpiringModal.vue";
import EhotelCheckout from "./Reservation/EhotelCheckout.vue";
import NuiteeCheckout from "./Reservation/NuiteeCheckout.vue";
import CultswitchCheckout from "./Reservation/CultswitchCheckout.vue";
import BookingEngineDateSelector from "./Widgets/BookingEngineDateSelector.vue";
import { applyPropertyPageSeo } from "../helpers/theme";
import { stashBookingConfirmation } from "../helpers/bookingConfirmation";
import {
  applyBookingFormValidationErrors,
  consumeBookingValidationErrors,
} from "../helpers/bookingValidation";
import {
  buildPurchaseAnalyticsPayload,
  stashPurchaseAnalytics,
  trackCompleteBooking,
  trackUpsellProduct,
} from "../helpers/analytics";
export default {
  name: "Reservation",
  components: {
    Spinner,
    Multiselect,
    EhotelCheckout,
    NuiteeCheckout,
    RateExpiringModal,
    BookingCheckout,
    CultswitchCheckout,
    BookingEngineDateSelector,
  },
  data() {
    return {
      fetching: false,
      step: "extra",
      noExtra: false,
      checkoutData: null,
      extras: [],
      collectCardDetails: true,
      processingBooking: false,
      hasReservationExpired: false,
      paymentExpiresAt: null,
      paymentExpiryTimer: null,
      paymentCountdownInterval: null,
      paymentTimeLeftMs: 0,
    };
  },
  async created() {
    this.fetching = true;
    const { id } = this.$route.params;
    const { tab } = this.$route.query;
    let checkoutData = sessionStorage.getItem(`checkoutData-${id}`);
    checkoutData = JSON.parse(checkoutData) || null;
    if (
      !checkoutData ||
      moment(checkoutData.date.checkIn).isBefore(
        moment(new Date()).format("YYYY-MM-DD")
      )
    ) {
      this.$router.push(`/listing/${id}`);
      return;
    }
    this.checkoutData = checkoutData;

    this.checkCreditCardCollection();

    if (checkoutData.distributorType === "A") {
      const { data } = await this.findCultswitchAvailability({
        check_in: checkoutData.date.checkIn,
        check_out: checkoutData.date.checkOut,
        property_id: id,
        promo_code: checkoutData.promoCode,
      });
      this.extras = data?.data?.extras || [];
      if (!this.extras.length || tab === "summary") {
        this.step = "summary";
        this.noExtra = true;
      } else {
        let totalGuests = 0;
        get(checkoutData, "cart", []).forEach((item) => {
          get(item, "guests", []).forEach((guest) => {
            totalGuests += get(guest, "guestCount", 0);
            get(guest, "variations", []).forEach((variation) => {
              totalGuests += get(variation, "quantity", 0);
            });
          });
        });

        this.extras = this.extras.map((extra) => {
          if (extra.extraCode !== "forPerPersonPerNight") return extra;
          return {
            ...extra,
            roomRates: Object.fromEntries(
              Object.entries(extra.roomRates).map(([key, value]) => [
                key,
                { ...value, totalAmount: value.totalAmount * totalGuests },
              ])
            ),
          };
        });
      }
    } else {
      this.noExtra = true;
      this.step = "summary";
    }

    if (!this.settings || Object.keys(this.settings).length === 0) {
      this.getPropertySettings(id);
    }

    this.fetching = false;
    if (this.checkoutData?.property) {
      applyPropertyPageSeo(this.checkoutData.property);
    }
    this.addMandatoryExtras();
    this.initializePaymentTimer();
    if (!this.documents || Object.keys(this.documents).length === 0) {
      await this.fetchPropertyDocuments(id);
    }

    if (this.step === "summary") {
      await this.$nextTick();
      this.restoreBookingValidationErrors();
    }
  },
  beforeDestroy() {
    this.clearPaymentTimer();

  },
  computed: {
    ...mapState("property", ["settings", "documents"]),
    guestData() {
      const { distributorType, cart } = this.checkoutData;
      if (distributorType === "B") {
        const item = cart[0];
        return { rooms: item.quantity, persons: item.guests };
      }
      return null;
    },
    formattedTimeLeft() {
      const totalSeconds = Math.max(0, Math.ceil(this.paymentTimeLeftMs / 1000));
      const minutes = Math.floor(totalSeconds / 60)
        .toString()
        .padStart(2, "0");
      const seconds = (totalSeconds % 60).toString().padStart(2, "0");
      return `${minutes}:${seconds}`;
    },
  },
  methods: {
    ...mapActions("booking", [
      "processCultswitchBooking",
      "processEhotelBooking",
    ]),
    ...mapActions("property", [
      "findCultswitchAvailability",
      "getPropertySettings",
      "fetchPropertyDocuments",
    ]),
    getCheckoutFormComponent() {
      const { distributorType, provider } = this.checkoutData || {};
      if (distributorType === "A") {
        return this.$refs.cultswitchForm;
      }
      if (distributorType === "B" && provider === "NUITEE") {
        return this.$refs.nuiteeForm;
      }
      if (distributorType === "B") {
        return this.$refs.ehotelForm;
      }
      return null;
    },
    restoreBookingValidationErrors() {
      const fieldErrors = consumeBookingValidationErrors(this.$route.params.id);
      if (!fieldErrors) {
        return;
      }

      applyBookingFormValidationErrors(this.getCheckoutFormComponent(), fieldErrors);
    },
    handleBookingSubmissionError(error) {
      if (error?.fieldErrors) {
        applyBookingFormValidationErrors(
          this.getCheckoutFormComponent(),
          error.fieldErrors
        );
      }

      this.$toastr.e(
        "Error",
        error?.message || this.$t("reservation.reservationError")
      );
    },
    paymentExpiryStorageKey() {
      return `checkoutPaymentExpiry-${this.$route.params.id}`;
    },
    clearPaymentTimer() {
      if (this.paymentExpiryTimer) {
        clearTimeout(this.paymentExpiryTimer);
        this.paymentExpiryTimer = null;
      }
      if (this.paymentCountdownInterval) {
        clearInterval(this.paymentCountdownInterval);
        this.paymentCountdownInterval = null;
      }
    },
    updateCountdown() {
      if (!this.paymentExpiresAt) {
        this.paymentTimeLeftMs = 0;
        return;
      }
      this.paymentTimeLeftMs = Math.max(0, this.paymentExpiresAt - Date.now());
    },
    initializePaymentTimer() {
      const key = this.paymentExpiryStorageKey();
      const existing = Number(sessionStorage.getItem(key));
      const now = Date.now();
      const isValidExistingExpiry =
        Number.isFinite(existing) && existing > now;
      this.paymentExpiresAt = isValidExistingExpiry
        ? existing
        : now + paymentExpiryMs;

      if (!isValidExistingExpiry) {
        sessionStorage.setItem(key, this.paymentExpiresAt.toString());
      }
      const remainingMs = Math.max(0, this.paymentExpiresAt - now);
      this.clearPaymentTimer();
      this.updateCountdown();
      this.paymentCountdownInterval = setInterval(this.updateCountdown, 1000);
      this.paymentExpiryTimer = setTimeout(this.expireReservationFlow, remainingMs);
    },
    expireReservationFlow() {
      if (this.hasReservationExpired) return;
      this.hasReservationExpired = true;
      this.processingBooking = false;
      this.clearPaymentTimer();

      const { id } = this.$route.params;
      sessionStorage.removeItem(`checkoutData-${id}`);
      sessionStorage.removeItem(this.paymentExpiryStorageKey());
      if (this.$refs.rateExpiringModal) {
        this.$refs.rateExpiringModal.showExpiryModal();
      }
    },
    handleExpiredAcknowledge() {
      this.$router.push(`/listing/${this.$route.params.id}`);
    },
    formatDate(date) {
      return moment(date, "YYYY-MM-DD").format("DD-MM-YYYY");
    },
    handleExtrasUpdated(extra) {
      this.removeExtra(extra);
    },
    handleRoomsUpdated() {
      this.checkCreditCardCollection();
    },
    addMandatoryExtras() {
      const mandatoryExtras = this.extras.filter((extra) => extra.isMandatory);
      mandatoryExtras.forEach((extra) => {
        if (this.checkoutData.extras.find((e) => e.id === extra.id)) return;
        this.addExtra(extra, 1);
      });
    },
    checkCreditCardCollection() {
      if (!this.checkoutData) return;
      const { provider, distributorType, cart } = this.checkoutData;
      if (distributorType === "A") {
        this.collectCardDetails = false;
        cart.forEach((item) => {
          const { bookingGuaranteeCode } = item.product;
          if (bookingGuaranteeCode && bookingGuaranteeCode.toString() === "3") {
            this.collectCardDetails = true;
          }
        });
      } else if (distributorType === "B" && provider === "NUITEE") {
        this.collectCardDetails = false;
      }
    },
    async proceedWithBooking() {
      if (this.hasReservationExpired) {
        if (this.$refs.rateExpiringModal) {
          this.$refs.rateExpiringModal.showExpiryModal();
        }
        return;
      }
      const { id } = this.$route.params;
      if (this.step === "extra") {
        sessionStorage.setItem(
          `checkoutData-${id}`,
          JSON.stringify(this.checkoutData)
        );
        this.step = "summary";
      } else if (this.step === "summary") {
        let component;
        const { distributorType, provider } = this.checkoutData;
        if (distributorType === "A") {
          component = this.$refs.cultswitchForm;
        } else if (distributorType === "B" && provider === "NUITEE") {
          component = this.$refs.nuiteeForm;
        } else if (distributorType === "B") {
          component = this.$refs.ehotelForm;
        }
        const observer = component.$refs.observer;
        const isValid = await observer.validate();
        if (isValid) {
          if (this.collectCardDetails) {
            this.processingBooking = true;
            document
              .querySelector("#pci_iframe")
              .contentWindow.postMessage(
                "validate",
                "https://service.pcibooking.net"
              );
          } else {
            if (provider === "NUITEE" && distributorType === "B") {
              const bookingData = component.getData();
              const successData = {
                type: distributorType,
                total: this.$refs.bookingCheckout.totalAmount,
                currency: this.$refs.bookingCheckout.currencyCode,
                propertyId: id,
              };
              await trackCompleteBooking(this.checkoutData, successData.total);
              sessionStorage.setItem(
                `checkoutData-${id}`,
                JSON.stringify({
                  ...this.checkoutData,
                  bookingData,
                  successData,
                })
              );
              document.querySelector(".lp-submit-button").click();
            } else {
              this.processingBooking = true;
              await this.completeBooking();
            }
          }
        } else if (!isValid) {
          const firstErrorField = Object.values(observer.refs).find(
            (el) => el.errors.length
          );

          if (firstErrorField) {
            firstErrorField.$el.scrollIntoView({
              behavior: "smooth",
              block: "center",
            });
          }
        }
      }
    },
    cultswitchProductPrice(product, guests = 1) {
      let prices = product.roomRates;
      if (prices[guests.toString()]) {
        return prices[guests.toString()].totalAmount;
      }
      prices = Object.values(prices);
      return prices.length ? prices[0].totalAmount : 0;
    },
    cultswitchQtyOption(extra) {
      return Array.from({ length: extra.numberOfUnits + 1 }, (_, i) => i);
    },
    addExtra(extra, quantity = 1) {
      if (quantity === 0) {
        this.removeExtra(extra);
        return false;
      }
      const idx = this.checkoutData.extras.findIndex(
        ({ id }) => extra.id === id
      );
      const price = this.cultswitchProductPrice(extra, quantity);
      if (idx < 0) {
        this.checkoutData.extras.push({
          id: extra.id,
          quantity,
          price,
          extra,
          currencyCode: this.checkoutData.cart?.[0]?.currencyCode || "",
        });
      } else {
        this.checkoutData.extras[idx]["quantity"] = quantity;
        this.checkoutData.extras[idx]["price"] = price;
        this.checkoutData.extras[idx]["currencyCode"] =
          this.checkoutData.cart?.[0]?.currencyCode || "";
      }
      trackUpsellProduct({
        ...extra,
        quantity,
        price,
        currencyCode: this.checkoutData.cart?.[0]?.currencyCode || "",
      });
    },
    removeExtra(extra) {
      const idx = this.checkoutData.extras.findIndex(
        ({ id }) => extra.id === id
      );
      this.checkoutData.extras.splice(idx, 1);
    },
    getExtraValue(id) {
      const item = this.checkoutData.extras.find((extra) => extra.id === id);
      return item ? item.quantity : 0;
    },
    extraQuantityChanged(qty, extra) {
      this.addExtra(extra, qty);
    },
    extraImages(extra) {
      const images = {};
      extra.media.forEach((image) => {
        images[image.id] = image;
      });

      return Object.values(images).sort((a, b) => a.sortOrder - b.sortOrder);
    },
    async completeBooking(cardDetails = null) {
      let bookerEmail = "";
      let reservationId = "";
      const totalAmount = this.$refs.bookingCheckout?.totalAmount;
      const currencyCode = this.$refs.bookingCheckout?.currencyCode;
      try {
        await trackCompleteBooking(this.checkoutData, totalAmount);

        if (this.checkoutData.distributorType === "A") {
          const bookingData = this.$refs.cultswitchForm.getData();
          bookerEmail = bookingData.booker.email;
          const { data } = await this.processCultswitchBooking({
            ...this.checkoutData,
            ...bookingData,
            propertyId: this.$route.params.id,
            payment: cardDetails,
          });
          if (data.paylink) {
            window.location.href = data.paylink;
            return;
          }
          reservationId = data.reservationId;
        } else if (this.checkoutData.distributorType === "B") {
          const bookingData = this.$refs.ehotelForm.getData();
          bookerEmail = bookingData.bookerDetails.email;
          const { data } = await this.processEhotelBooking({
            ...this.checkoutData,
            ...bookingData,
            propertyId: this.$route.params.id,
            payment: cardDetails,
          });
          reservationId = data.data.reservationId;
        }

        stashPurchaseAnalytics(
          buildPurchaseAnalyticsPayload({
            checkoutData: this.checkoutData,
            transactionId: reservationId,
            value: totalAmount,
            currency: currencyCode,
          })
        );

        stashBookingConfirmation({ reservationId, email: bookerEmail });
        this.$toastr.s("Success", this.$t("reservation.reservationSuccess"));
        sessionStorage.removeItem(`checkoutData-${this.$route.params.id}`);
        sessionStorage.removeItem(this.paymentExpiryStorageKey());
        this.$router.push({
          path: "/reservation_success",
          query: {
            type: this.checkoutData.distributorType,
            reservationId,
            total: totalAmount,
            currency: currencyCode,
            propertyId: this.$route.params.id,
          },
        });
      } catch (error) {
        this.handleBookingSubmissionError(error);
      } finally {
        this.processingBooking = false;
      }
    },
    clearSelection() {
      const { id } = this.$route.params;
      sessionStorage.removeItem(`checkoutData-${id}`);
      sessionStorage.removeItem(this.paymentExpiryStorageKey());
      this.$router.push(`/listing/${id}`);
    },
    searchAvailability(data) {
      const { checkIn, checkOut, ehotelGuestData, nuiteeGuestsData } = data;
      const nuiteeGuest = JSON.stringify(nuiteeGuestsData);
      const eHotelGuest = JSON.stringify(ehotelGuestData);
      this.$router.push(
        `/listing/${this.$route.params.id}?
        checkIn=${checkIn}
        &checkOut=${checkOut}
        &nuiteeGuest=${nuiteeGuest}
        &eHotelGuest=${eHotelGuest}
        &findAvailability=true`
      );
    },
  },
};
</script>
